import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';

import { Button } from '@/components/ui';
import { clear, useLookAlong } from '@/lib/impersonation';

/**
 * The amber band across the whole app while an operator is looking along with
 * a customer. It must be impossible to forget you are in someone else's
 * account, so it sits above every route — including the login screen — it is
 * sticky like the rest of the app's chrome, and it carries the amber as its
 * BACKGROUND rather than as a tint.
 *
 * The first version used `--tone-warn-bg`, which is #FBF7EC on a #FBFBF9 page:
 * a different white. Amber on the page ground, both from tokens, is the pair
 * that reads as a band in either theme — `--tone-warn` flips to a light amber
 * in dark mode and `--bg-primary` flips with it, so the contrast survives the
 * swap and no colour is hard-coded.
 *
 * The stop button discards the tab's token and nothing else. It does NOT call
 * logout(): auth.ts sends that request with no Authorization header, so the
 * backend cannot tell it apart from the operator signing out in their own tab
 * and would revoke their real session. It also leaves `clearToken()` alone —
 * that is the operator's own session, and stopping a look-along is not signing
 * out. Then it reloads the app root, which is what puts the operator back in
 * their own account: a fresh load with no look-along lets restoreSession run.
 */
export function ImpersonationBanner() {
  const { t, i18n } = useTranslation();
  const lookAlong = useLookAlong();

  if (!lookAlong) return null;

  const stop = () => {
    clear();
    window.location.href = import.meta.env.BASE_URL;
  };

  // Only shown when we were actually told when this ends. A look-along whose
  // expiry we do not know shows no clock rather than a made-up one.
  const ends = lookAlong.expiresAt ? new Date(lookAlong.expiresAt) : null;
  const until = ends && !Number.isNaN(ends.getTime())
    ? t('admin.lookAlong.until', {
        tijd: ends.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }),
      })
    : null;

  return (
    <div
      role="status"
      className="sticky top-0 z-50 flex items-center justify-between gap-md px-lg py-sm
                 bg-tone-warn text-bg-primary text-body-sm font-medium"
    >
      <span className="flex items-center gap-sm min-w-0">
        <Eye className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="truncate">
          {lookAlong.tenantName
            ? t('admin.lookAlong.banner', { klant: lookAlong.tenantName })
            : t('admin.lookAlong.bannerUnknown')}
          {until ? ` ${until}` : ''}
        </span>
      </span>
      <Button variant="secondary" size="sm" onClick={stop}>
        {t('admin.lookAlong.stop')}
      </Button>
    </div>
  );
}
